/* eslint-disable react/no-unescaped-entities */
import { memo, useMemo } from 'react';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Link from '@atlaskit/link';
import * as colors from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { MetricUsageStatusType, SERVICE_CONTAINERS } from '../../constants';
import { MetricsUsageCard, MetricsUsageSideIcon } from '../styled';

export interface Props {
  status: MetricUsageStatusType;
  stepSize: number;
  warningContainers: string[];
}

const warningMessageData = [
  {
    key: 1,
    text: ({ warningContainers }: any) => (
      <>Container '{warningContainers[0]}' is nearing the memory limit.</>
    ),
  },
  {
    key: 2,
    text: ({ warningContainers }: any) => (
      <>
        Container '{warningContainers[0]}' and '{warningContainers[1]}' are
        nearing the memory limit.
      </>
    ),
  },
  {
    key: 3,
    text: ({ warningContainers, numSecondaryContainers }: any) => (
      <>
        {warningContainers[0]} and {numSecondaryContainers} other are nearing
        the memory limit.
      </>
    ),
  },
];

const errorMessageData = [
  {
    key: 1,
    text: ({ warningContainers }: any) => (
      <>Container '{warningContainers[0]}' exceeded the memory limit.</>
    ),
  },
  {
    key: 2,
    text: ({ warningContainers }: any) => (
      <>
        Container '{warningContainers[0]}' and '{warningContainers[1]}' exceeded
        the memory limit.
      </>
    ),
  },
  {
    key: 3,
    text: ({ warningContainers, numSecondaryContainers }: any) => (
      <>
        Container '{warningContainers[0]}' and {numSecondaryContainers} others
        exceeded the memory limit.
      </>
    ),
  },
];

export const MetricsUsageMessage = memo(
  ({ status, stepSize, warningContainers }: Props) => {
    const metricMessage = useMemo(() => {
      const numOomContainers =
        warningContainers.length > 3 ? 3 : warningContainers.length;

      return status === MetricUsageStatusType.Error
        ? errorMessageData.filter(l => l.key === numOomContainers)[0]
        : warningMessageData.filter(l => l.key === numOomContainers)[0];
    }, [status, warningContainers]);

    const numSecondaryContainers = warningContainers.length - 1;

    if (stepSize === undefined) {
      return null;
    }

    return (
      <MetricsUsageCard usageStatus={status}>
        <MetricsUsageSideIcon>
          {status === MetricUsageStatusType.Warning && (
            <StatusWarningIcon
              label={status}
              size="small"
              color={token('color.icon.warning')}
            />
          )}
          {status === MetricUsageStatusType.Error && (
            <StatusErrorIcon
              label={status}
              size="small"
              color={token('color.icon.danger')}
            />
          )}
        </MetricsUsageSideIcon>
        <div>
          <strong>{status}</strong>
          <br />
          {metricMessage.text({
            warningContainers,
            numSecondaryContainers,
          })}
          <br />
          {stepSize < 2 && (
            <>
              <strong>Hint:</strong> You can define the step size using
              <br />
              <pre
                style={{
                  background: MetricUsageStatusType.Error
                    ? colors.Y50
                    : '#FFEBE6',
                }}
              >
                size: 2x
              </pre>{' '}
              to double the memory
              <br />
              <br />
            </>
          )}
          <Link href={SERVICE_CONTAINERS} target="_blank">
            Learn more about service memory limits
          </Link>
        </div>
      </MetricsUsageCard>
    );
  }
);
